'use client';

import { Button } from '@florencelabs/button';
import {
  MaterialSymbol,
  type MaterialSymbolName,
  type MaterialSymbolProps,
} from '@florencelabs/icons';
import { useRouter } from 'next/navigation';
import { ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPhone } from '@provider-directory/components/phone-number';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import { getSchedulingFlowType } from '@provider-directory/contexts/DecisionTreeContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { useAnalytics } from '../analytics/analytics-provider';
import { useOrgContext } from '../org-context/org-context-provider';

export interface SchedulingErrorTemplateProps {
  title: string;
  code?: string | null;
  body: string | React.ReactNode;
  children?: React.ReactNode;
  icon?: MaterialSymbolName | MaterialSymbolProps;
}

export const SceneErrorTemplate = ({
  code,
  title,
  body,
  children,
  icon,
}: SchedulingErrorTemplateProps): ReactElement => {
  return (
    <div className={'mx-auto flex w-full max-w-lg flex-col px-6 pt-20'}>
      {icon && (
        <div className={'flex w-full pb-6'}>
          {typeof icon === 'string' ? (
            <MaterialSymbol
              size={40}
              name={icon}
              className={'text-primary-950'}
            />
          ) : (
            <MaterialSymbol
              className={'text-primary-950'}
              size={40}
              {...icon}
            />
          )}
        </div>
      )}
      <div className={'flex w-full flex-col gap-3 pb-10'}>
        {code && <p className={'text-body-small-bold'}>{code.toUpperCase()}</p>}
        <h1 className={'font-serif text-display-xlarge'}>{title}</h1>
        {typeof body === 'string' ? (
          <p className={'text-body-medium text-gray-900'}>{body}</p>
        ) : (
          body
        )}
      </div>
      {children}
    </div>
  );
};

export const AppointmentUnavailableError = (): ReactElement => {
  const router = useRouter();
  const { t } = useTranslation();
  const { getProvidersUrl } = useOrgContext();
  const { provider } = useProvider();
  const { appointment } = useAppointment();

  const analytics = useAnalytics();
  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    analytics.slotUnavailableEvent({
      provider,
      appointment,
      schedulingFlowType: getSchedulingFlowType(),
      linkContextPage: 'SchedulingReview',
    });
  }, [analytics, provider, appointment]);

  return (
    <SceneErrorTemplate
      icon={'error_fill'}
      title={t('schedule.errors.unavailable.title')}
      body={t('schedule.errors.unavailable.body')}
    >
      <div className={'flex gap-2'}>
        <Button
          onPress={async () => {
            await analytics.track('CLICKED_SELECT_ANOTHER_TIME', {
              appType: 'provider-directory',
              linkContextPage: 'SchedulingReview',
              linkContextComponent: 'AppointmentSummary',
            });
            router.replace('select-time');
          }}
          variant={'clientMain'}
        >
          {t('schedule.selectATime')}
        </Button>
        <Button
          onPress={() => {
            router.replace(getProvidersUrl());
          }}
          variant={'systemOutline'}
        >
          {t('schedule.backToSearch')}
        </Button>
      </div>
    </SceneErrorTemplate>
  );
};

export const DefaultSchedulingError = ({
  errorCode,
}: {
  errorCode?: string | null;
}): ReactElement => {
  const router = useRouter();
  const { t } = useTranslation();
  const {
    getProvidersUrl,
    config: { scheduling },
  } = useOrgContext();
  const { provider } = useProvider();
  const { appointment } = useAppointment();

  const analytics = useAnalytics();
  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    analytics.schedulingErrorEvent({
      provider,
      appointment,
      schedulingFlowType: getSchedulingFlowType(),
      linkContextPage: 'SchedulingReview',
      errorCode,
    });
  }, [analytics, provider, appointment, errorCode]);

  return (
    <SceneErrorTemplate
      icon={'error_fill'}
      code={errorCode}
      title={t('schedule.errors.default.title')}
      body={
        <p className={'text-body-medium'}>
          {t('schedule.errors.default.body')}{' '}
          <strong>{formatPhone(scheduling.displayNumber)}</strong>
        </p>
      }
    >
      <div className={'flex gap-2'}>
        <Button
          variant={'clientMain'}
          onPress={async () => {
            await analytics.track('CLICKED_BACK_TO_SEARCH', {
              appType: 'provider-directory',
              linkContextPage: 'SchedulingReview',
              linkContextComponent: 'AppointmentSummary',
            });
            router.replace(getProvidersUrl());
          }}
        >
          {t('schedule.backToSearchResults')}
        </Button>
      </div>
    </SceneErrorTemplate>
  );
};
